import React from 'react';
import { Popup, PopupProps } from './Popup';

export type AlertType = 'success' | 'info' | 'warning' | 'error';

// Map alert types to theme colors from the project
const typeToColor: { [key: string]: { bg: string, fill: string } } = {
  success: { 
    bg: 'bg-green-100',
    fill: '#10B981' // Green-500 from the project
  },
  error: { 
    bg: 'bg-red-100',
    fill: '#EF4444' // Red-500 from the project
  },
  warning: { 
    bg: 'bg-yellow-100',
    fill: '#F59E0B' // Yellow-500 from the project
  },
  info: { 
    bg: 'bg-blue-100',
    fill: '#3B82F6' // Blue-500 from the project
  },
};

// Simple SVG Icon components for each alert type
const SuccessIcon = ({ fill = '#10B981' }: { fill?: string }) => (
  <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="30" fill={fill} />
    <path d="M27 37L21 31L18 34L27 43L46 24L43 21L27 37Z" fill="white" />
  </svg>
);

const ErrorIcon = ({ fill = '#EF4444' }: { fill?: string }) => (
  <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="30" fill={fill} />
    <path d="M43 24L40 21L32 29L24 21L21 24L29 32L21 40L24 43L32 35L40 43L43 40L35 32L43 24Z" fill="white" />
  </svg>
);

const WarningIcon = ({ fill = '#F59E0B' }: { fill?: string }) => (
  <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="30" fill={fill} />
    <path d="M32 20V40M32 46V48" stroke="white" strokeWidth="6" strokeLinecap="round" />
  </svg>
);

const InfoIcon = ({ fill = '#3B82F6' }: { fill?: string }) => (
  <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="30" fill={fill} />
    <path d="M32 16V18M32 24V48" stroke="white" strokeWidth="6" strokeLinecap="round" />
  </svg>
);

// Map alert types to their respective SVG components
const typeToIcon = {
  success: SuccessIcon,
  error: ErrorIcon,
  warning: WarningIcon,
  info: InfoIcon,
};

export interface AlertPopupProps extends Omit<PopupProps, 'children'> {
  type: AlertType;
  message: string;
  iconSrc?: string;
  onClose?: () => void;
  popupId?: string; // Optional ID for popup stacking management
  actionButton?: {
    text: string;
    onClick: () => void;
  };
}

export const AlertPopup: React.FC<AlertPopupProps> = ({
  type,
  message,
  iconSrc, // Not used anymore but kept for backward compatibility
  onClose,
  popupId,
  actionButton,
  ...props
}) => {
  // Get the styling for this alert type
  const colors = typeToColor[type] || { 
    bg: 'bg-gray-100',
    fill: '#6B7280' // Gray-500
  };
  
  // Get the icon component for this alert type
  const IconComponent = typeToIcon[type];
  
  return (
    <Popup
      visible={true}
      onCancel={onClose}
      className="alert-popup"
      footer={null}
      closable={true}
      maskClosable={true}
      closeIcon={<div className="close-icon">×</div>}
      popupId={popupId}
      width={320} // Standardized width to match default popup width
      // Override z-index to ensure AlertPopup always appears on top of SmartOTPModal
      styles={{
        mask: { zIndex: 30000 },
        wrapper: { zIndex: 30010 },
        content: { zIndex: 30020 }
      }}
      {...props}
    >
      <div className="flex flex-col items-center justify-center p-4" style={{ pointerEvents: 'auto' }}>
        <div className={`alert-icon-container ${colors.bg} rounded-full p-4 mb-4 flex items-center justify-center`}>
          <IconComponent fill={colors.fill} />
        </div>
        {(() => {
          const isHtml = typeof message === 'string' && /<\/?[a-z][\s\S]*>/i.test(message);
          return isHtml ? (
            <div className="text-center text-base font-medium" dangerouslySetInnerHTML={{ __html: message }} />
          ) : (
            <div className="text-center text-base font-medium">{message}</div>
          );
        })()}
        {actionButton && (
          <button 
            onClick={() => {
              if (onClose) onClose();
              actionButton.onClick();
            }}
            className="p-8 h-[50px] rounded-[15px] bg-[#5A4099] text-white font-bold py-3 hover:opacity-90 mt-8"
          >
            {actionButton.text}
          </button>
        )}
      </div>
    </Popup>
  );
};
